import React from "react";
import {
  Box,
  Flex,
  Text,
  Divider,
  Badge,
  Stack,
  Button,
  useColorModeValue,
  Icon,
  HStack,
  VStack,
  Tooltip,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from "@chakra-ui/react";
import { 
  Package, 
  Truck, 
  Clock,
  Shield,
  XCircle,
  Info,
  IndianRupee,
  AlertCircle,
  CheckCircle
} from "lucide-react";

const DetailProductCard = ({ el, type, createCustomerCardData, expanded, setExpanded }) => {
  const cardData = createCustomerCardData(el);

  // Chakra color modes
  const bg = useColorModeValue("white", "gray.800");
  const border = useColorModeValue("gray.200", "gray.600");
  const labelColor = useColorModeValue("gray.600", "gray.400");
  const valueColor = useColorModeValue("gray.800", "gray.100");
  const highlightColor = useColorModeValue("blue.600", "blue.300");
  const headerBg = useColorModeValue("blue.50", "blue.900");
  const hoverBg = useColorModeValue("gray.50", "gray.700");

  const totalOrdered = parseInt(el.count) || 0;
  const totalDelivered = parseInt(el.delivered_quantity) || 0;
  const totalCancelled = parseInt(el.cancelled_quantity) || 0;

  const hasExtraCylinders = Array.isArray(el.extra_cylinders) && el.extra_cylinders.length > 0;

  const baseEmpties =
    el.type === "1"
      ? parseInt(el.returnclinder) || 0
      : parseInt(el.empty_received) || 0;
  
      const isType4 = "4";

const totalEmpties = !isType4
  ? Number(el.returnclinder) || 0
  : (Number(el.empty_received) || 0) +
    (el.extra_cylinders?.reduce(
      (sum, cyl) => sum + (Number(cyl.extra_received_empty) || 0),
      0
    ) || 0);

    
  
  

  const getStatusInfo = () => {
    if (totalDelivered === totalOrdered) return { color: "green", label: "Delivered", icon: CheckCircle };
    if (totalCancelled === totalOrdered) return { color: "red", label: "Cancelled", icon: XCircle };
    if (totalDelivered > 0 || totalCancelled > 0) return { color: "orange", label: "Partial", icon: AlertCircle };
    return { color: "blue", label: "Pending", icon: Clock };
  };
  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  const formatDateTime = (dateTime) => {
    if (!dateTime || dateTime === "0000-00-00 00:00:00") return "N/A";
    const date = new Date(dateTime);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  };

  return (
    <Box
      w="full"
      maxW="420px"
      borderWidth="1px"
      borderColor={border}
      borderRadius="2xl"
      bg={bg}
      boxShadow="sm"
      p={6}
      transition="all 0.3s ease"
      _hover={{ 
        boxShadow: "lg", 
        transform: "translateY(-2px)",
        borderColor: highlightColor
      }}
      position="relative"
      overflow="hidden"
    >
      {/* Status Ribbon */}
      <Box
        position="absolute"
        top={4}
        right={-8}
        bg={`${statusInfo.color}.500`}
        color="white"
        px={8}
        py={1}
        transform="rotate(45deg)"
        fontSize="xs"
        fontWeight="bold"
        zIndex={1}
      >
        {statusInfo.label}
      </Box>

      {/* Header */}
      <Flex justify="space-between" align="flex-start" mb={4}>
        <Flex align="flex-start" gap={3} flex={1}>
          <Box p={2} bg={headerBg} borderRadius="lg" color={highlightColor}>
            <Icon as={Package} boxSize={5} />
          </Box>
          <VStack align="flex-start" spacing={1} flex={1}>
            <Text 
              fontWeight="semibold" 
              color={valueColor} 
              fontSize="md"
              lineHeight="short"
              noOfLines={2}
            >
              {el.product_name || "Product Name"}
            </Text>
          </VStack>
        </Flex>
      </Flex>

      {/* Tabs for Product & Extra Cylinders */}
      <Tabs variant="enclosed" colorScheme="blue">
        <TabList>
          <Tab fontSize="sm">Product Details</Tab>
          {el.extra_cylinders?.length > 0 && <Tab fontSize="sm">Extra Cylinders</Tab>}
        </TabList>

        <TabPanels>
          {/* 🟢 PRODUCT DETAILS PANEL */}
          <TabPanel px={0}>
            <HStack justify="space-between" bg={hoverBg} p={3} borderRadius="lg" mb={4}>
              <VStack spacing={0}>
                <Text fontSize="xs" color={labelColor}>ORDERED</Text>
                <Text fontSize="lg" fontWeight="bold" color={valueColor}>{totalOrdered}</Text>
              </VStack>
              <VStack spacing={0}>
                <Text fontSize="xs" color={labelColor}>DELIVERED</Text>
                <Text fontSize="lg" fontWeight="bold" color="green.500">{totalDelivered}</Text>
              </VStack>
              <VStack spacing={0}>
                <Text fontSize="xs" color={labelColor}>CANCELLED</Text>
                <Text fontSize="lg" fontWeight="bold" color="red.500">{totalCancelled}</Text>
              </VStack>
              <VStack spacing={0}>
                <Text fontSize="xs" color={labelColor}>EMPTIES</Text>
                <Text fontSize="lg" fontWeight="bold" color="blue.500">
                  {totalEmpties}
                </Text>
              </VStack>
            </HStack>

            {/* Existing Details */}
            {expanded && cardData?.displayData && (
              <Box mb={4}>
                <Text fontSize="sm" fontWeight="semibold" color={valueColor} mb={3}>
                  DETAILED BREAKDOWN
                </Text>
                <Stack spacing={2}>
                  {cardData.displayData.map((item, index) => (
                    <Flex
                      key={index}
                      justify="space-between"
                      align="center"
                      py={2}
                      px={3}
                      bg={index % 2 === 0 ? hoverBg : "transparent"}
                      borderRadius="md"
                      _hover={{ bg: headerBg }}
                    >
                      <HStack>
                        <Text fontSize="sm" color={labelColor}>{item.key}</Text>
                        {item.tooltip && (
                          <Tooltip label={item.tooltip} fontSize="xs">
                            <Box><Icon as={Info} boxSize={3} color={labelColor} /></Box>
                          </Tooltip>
                        )}
                      </HStack>
                      <Text fontSize="sm" fontWeight={item.valueWeight || "semibold"} color={valueColor}>
                        {item.value}
                      </Text>
                    </Flex>
                  ))}
                </Stack>
              </Box>
            )}
          </TabPanel>

          {/* 🟡 EXTRA CYLINDERS PANEL */}
          {el.extra_cylinders?.length > 0 && (
            <TabPanel px={0}>
              {el.extra_cylinders.map((extra, idx) => (
                <Box
                  key={idx}
                  borderWidth="1px"
                  borderColor={border}
                  borderRadius="lg"
                  p={3}
                  mb={3}
                  bg={hoverBg}
                >
                  <Text fontWeight="semibold" color={highlightColor}>
                    {extra.extra_product_name || "Extra Cylinder"}
                  </Text>
                  <Text fontSize="sm" color={labelColor}>
                    Empty Received: {extra.extra_received_empty}
                  </Text>
                  <Text fontSize="sm" color={labelColor}>
                    Delivered At: {formatDateTime(extra.deliver_datetime)}
                  </Text>
                </Box>
              ))}
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>

      <Divider my={4} />

      {/* Total */}
      <Box bg={headerBg} p={3} borderRadius="lg" mb={3} border="1px solid" borderColor={border}>
        <Flex justify="space-between" align="center">
          <Text fontSize="sm" fontWeight="semibold" color={valueColor}>
            Total Amount
          </Text>
          <Text fontSize="lg" fontWeight="bold" color={highlightColor}>
            ₹{(cardData?.calculatedTotal || 0).toFixed(2)}
          </Text>
        </Flex>
      </Box>

      {/* Actions */}
      <Flex justify="space-between" align="center">
        <Button
          size="sm"
          variant="ghost"
          colorScheme="blue"
          borderRadius="full"
          fontWeight="medium"
          leftIcon={<Icon as={expanded === el.id ? XCircle : Info} boxSize={4} />}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded === el.id ? "Show Less" : "Details"}
        </Button>

        <Badge colorScheme={statusInfo.color} fontSize="xs" borderRadius="full" px={3} py={1}>
          <HStack spacing={1}>
            <StatusIcon size={12} />
            <Text>{statusInfo.label}</Text>
          </HStack>
        </Badge>
      </Flex>

      {/* Timestamp */}
      {el.deliver_datetime && el.deliver_datetime !== "0000-00-00 00:00:00" && (
        <Box mt={3} pt={3} borderTop="1px solid" borderColor={border}>
          <Flex align="center" justify="space-between">
            <Text fontSize="xs" color={labelColor}>Last Updated</Text>
            <Text fontSize="xs" color={valueColor} fontWeight="medium">
              {formatDateTime(el.deliver_datetime)}
            </Text>
          </Flex>
        </Box>
      )}
    </Box>
  );
};

export default DetailProductCard;
